<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { api, type BgaSignalData, type SentimentHistoryPoint, type NewsTickerItem } from '../api/client'

const props = defineProps<{
  signal: BgaSignalData
  history: SentimentHistoryPoint[]
}>()

// Info popover state. Click the (i) icon → load today's headlines for the
// basket and toggle the panel. Headlines stay loaded across collapses for
// fast reopening; refresh implicitly when the popover opens after the news
// ticker store mutates (per-poll, every 5 min).
const showInfo = ref(false)
const headlines = ref<NewsTickerItem[]>([])
const headlinesLoading = ref(false)
const headlinesError = ref('')

async function loadHeadlines() {
  headlinesLoading.value = true
  headlinesError.value = ''
  try {
    headlines.value = await api.newsTicker(30)
  } catch (e: any) {
    headlinesError.value = e.message ?? 'Failed to load headlines'
  } finally {
    headlinesLoading.value = false
  }
}

function toggleInfo(ev: MouseEvent) {
  // The card itself is a link to /charts/sentiment. Stop the click here so
  // the (i) icon doesn't double-fire navigation.
  ev.preventDefault()
  ev.stopPropagation()
  showInfo.value = !showInfo.value
  if (showInfo.value && headlines.value.length === 0) loadHeadlines()
}

watch(showInfo, (v) => {
  // Reload when reopening after the panel was previously closed AND the
  // sentiment row updated (gauge value change implies the daily aggregator
  // cycled). Cheap heuristic; the live ticker poller already keeps the
  // server-side cache fresh.
  if (v && headlines.value.length > 0) loadHeadlines()
})

// — State derivation (matches the existing pill logic)
const sent = computed(() => props.signal.news_basket_sentiment)
const cov = computed(() => props.signal.news_basket_coverage_pct ?? 0)
const thr = computed(() => props.signal.news_sentiment_threshold ?? 0.15)
const minCov = computed(() => props.signal.news_sentiment_min_coverage_pct ?? 30)
const filterOn = computed(() => props.signal.news_sentiment_filter === true)
const blocking = computed(() => props.signal.news_sentiment_blocks_buy === true)

const state = computed<'OFF' | 'ON' | 'THIN' | 'BLOCK'>(() => {
  if (blocking.value) return 'BLOCK'
  if (!filterOn.value) return 'OFF'
  if (cov.value < minCov.value) return 'THIN'
  return 'ON'
})

const sentLabel = computed(() => {
  const v = sent.value
  if (v == null) return '—'
  if (v >= 0.20) return 'positive'
  if (v >= 0.05) return 'mild positive'
  if (v >= -0.05) return 'neutral'
  if (v >= -0.20) return 'mild negative'
  return 'negative'
})

// What WOULD the gate do today, regardless of filter on/off?
const gateWouldBlock = computed(() => {
  const v = sent.value
  if (v == null) return false
  return v <= thr.value && cov.value >= minCov.value
})

// — Mini sparkline data: last 7 days with non-zero coverage
const sparkPoints = computed(() => {
  const filtered = props.history
    .filter(p => p.sentiment != null && (p.coveragePct ?? 0) > 0)
    .slice(-7)
  if (filtered.length < 2) return []
  const vals = filtered.map(p => p.sentiment as number)
  const min = Math.min(...vals, thr.value, 0)
  const max = Math.max(...vals, thr.value, 0)
  const range = (max - min) || 1
  const w = 140, h = 32, pad = 2
  return filtered.map((p, i) => ({
    x: pad + (i / (filtered.length - 1)) * (w - 2 * pad),
    y: pad + (1 - ((p.sentiment as number) - min) / range) * (h - 2 * pad),
    raw: p,
  }))
})

const sparkPath = computed(() => {
  const pts = sparkPoints.value
  if (pts.length === 0) return ''
  return pts.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`).join(' ')
})

// Gauge: today's value mapped onto a [-1, +1] horizontal track. Threshold marker too.
function gaugePosition(v: number): number {
  return Math.max(0, Math.min(100, ((v + 1) / 2) * 100))
}
const todayGaugePct = computed(() => sent.value != null ? gaugePosition(sent.value) : 50)
const thrGaugePct = computed(() => gaugePosition(thr.value))

// Card-as-link nav. Plain left-click → SPA push to /charts/sentiment.
// Cmd/Ctrl/middle/shift fall through to browser default (open in new tab).
function goToChart(ev: MouseEvent) {
  if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey) return
  ev.preventDefault()
  window.history.pushState({}, '', '/charts/sentiment')
  window.dispatchEvent(new PopStateEvent('popstate'))
}
</script>

<template>
  <a
    href="/charts/sentiment"
    class="card"
    :class="`state-${state.toLowerCase()}`"
    :title="`Open sentiment chart — today: sent ${sent != null ? sent.toFixed(3) : '—'}, cov ${cov.toFixed(0)}%`"
    @click="goToChart"
  >
    <div class="card-row">
      <div class="status-block">
        <div class="status-label">
          News sentiment
          <span class="info-icon" @click="toggleInfo" :title="'Show details'">i</span>
        </div>
        <div class="status-badge">{{ state }}</div>
      </div>

      <div class="reading-block">
        <div class="sent-value" :class="(sent ?? 0) >= 0 ? 'pos' : 'neg'">
          {{ sent != null ? (sent >= 0 ? '+' : '') + sent.toFixed(3) : '—' }}
        </div>
        <div class="sent-label">{{ sentLabel }}</div>
      </div>

      <div class="gauge-block">
        <div class="gauge-track">
          <div class="gauge-zero" />
          <div class="gauge-thr" :style="{ left: thrGaugePct + '%' }" />
          <div class="gauge-now" :style="{ left: todayGaugePct + '%' }" />
        </div>
        <div class="gauge-labels">
          <span>−1</span><span>0</span><span class="thr-label">thr {{ thr.toFixed(2) }}</span><span>+1</span>
        </div>
      </div>

      <div class="cov-block">
        <div class="cov-label">Coverage</div>
        <div class="cov-bar">
          <div class="cov-fill" :style="{ width: Math.min(cov, 100) + '%' }" />
          <div class="cov-floor" :style="{ left: Math.min(minCov, 100) + '%' }" />
        </div>
        <div class="cov-value">{{ cov.toFixed(0) }}% / {{ minCov }}%</div>
      </div>

      <div class="spark-block">
        <div class="spark-label">7d trend</div>
        <svg class="spark" viewBox="0 0 140 32" preserveAspectRatio="none">
          <line v-if="sparkPath" x1="0" y1="16" x2="140" y2="16" stroke="#21262d" stroke-width="0.5" />
          <path v-if="sparkPath" :d="sparkPath" fill="none" stroke="#3da9fc" stroke-width="1.5" />
        </svg>
      </div>
    </div>

    <div class="footer-row">
      <span class="hint">
        Filter would
        <span :class="gateWouldBlock ? 'fail' : 'pass'">{{ gateWouldBlock ? 'BLOCK BUYs' : 'pass' }}</span>
        today
      </span>
    </div>

    <!-- (i) Info panel — comprehensive description, gate diagnostics, headlines.
         Click event swallowed so the surrounding <a> doesn't navigate. -->
    <div v-if="showInfo" class="info-panel" @click.stop @click.prevent>
      <p class="info-section-title">What this is</p>
      <p>
        Daily basket-weighted news sentiment from AlphaVantage NEWS_SENTIMENT.
        Each morning's pre-market window (05:00–08:00 UTC) the live poller persists articles to Postgres;
        at 09:00 UTC the daily aggregator rolls them up per ticker, weights by relevance × basket weight,
        and writes one summary row to <code>news_daily_basket_sentiment</code>. Today's row is what you see above.
      </p>

      <p class="info-section-title">Filter state</p>
      <ul class="info-bullets">
        <li>
          <strong>Filter:</strong>
          <span :class="filterOn ? 'pos' : 'dim'">{{ filterOn ? 'ENABLED' : 'DISABLED' }}</span>
          —
          {{ filterOn
            ? 'live trading gates BUYs when conditions below are met.'
            : 'observation only; sentiment values shown but no trading impact.' }}
        </li>
        <li>
          <strong>Today's value:</strong>
          <span :class="(sent ?? 0) >= 0 ? 'pos' : 'neg'">
            {{ sent != null ? (sent >= 0 ? '+' : '') + sent.toFixed(3) : 'no data' }}
          </span>
          ({{ sentLabel }})
        </li>
        <li>
          <strong>Threshold:</strong> sentiment ≤ <code>{{ thr.toFixed(2) }}</code> would trigger the gate.
          Currently
          <span :class="(sent ?? 0) <= thr ? 'fail' : 'pos'">
            {{ (sent ?? 0) <= thr ? 'AT or BELOW' : 'ABOVE' }} threshold
          </span>.
        </li>
        <li>
          <strong>Coverage:</strong> {{ cov.toFixed(1) }}% of basket has news today;
          floor is <code>{{ minCov }}%</code>.
          <span :class="cov >= minCov ? 'pos' : 'fail'">
            {{ cov >= minCov ? 'meets coverage floor' : 'BELOW coverage floor — gate suppressed' }}.
          </span>
        </li>
        <li>
          <strong>Right now:</strong>
          <span v-if="blocking" class="fail">GATE FIRING — BUYs are being blocked.</span>
          <span v-else-if="filterOn && cov < minCov" class="dim">
            Filter on but signal too thin (coverage {{ cov.toFixed(0) }}% &lt; floor {{ minCov }}%) — pass-through.
          </span>
          <span v-else-if="filterOn" class="pos">Filter on, sentiment above threshold — no gate.</span>
          <span v-else class="dim">Filter off — observation only.</span>
        </li>
      </ul>

      <p class="info-section-title">Today's headlines (basket + macro)</p>
      <div v-if="headlinesLoading" class="dim">Loading headlines…</div>
      <div v-else-if="headlinesError" class="fail">{{ headlinesError }}</div>
      <div v-else-if="headlines.length === 0" class="dim">No headlines for today's pre-market window.</div>
      <ul v-else class="headlines">
        <li v-for="h in headlines" :key="h.url + h.ticker" class="headline-row">
          <span class="hl-ticker">{{ h.ticker }}</span>
          <span
            class="hl-sent"
            :class="h.sentimentScore != null && h.sentimentScore >= 0 ? 'pos' : (h.sentimentScore != null ? 'neg' : 'dim')"
          >
            {{ h.sentimentScore != null
              ? (h.sentimentScore >= 0 ? '+' : '') + h.sentimentScore.toFixed(2)
              : '—' }}
          </span>
          <span class="hl-rel dim">rel {{ h.relevance.toFixed(2) }}</span>
          <a :href="h.url" target="_blank" rel="noopener" class="hl-title">{{ h.headline }}</a>
        </li>
      </ul>
      <p class="info-foot dim">
        Per-headline contribution to the daily aggregate ≈ <code>sentiment × relevance × basket_weight</code>.
        Aggregator filters relevance ≥ 0.30 before summing.
      </p>
    </div>
  </a>
</template>

<style scoped>
.card {
  display: block;
  border: 1px solid #21262d; border-radius: 6px; padding: 8px 10px;
  background: #0d1117; font-family: 'JetBrains Mono', monospace;
  /* Card is now an <a> — reset link styling and apply pointer affordance. */
  text-decoration: none; color: inherit; cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, transform 0.05s;
}
.card:hover { background: #11161e; }
.card:active { transform: translateY(1px); }
.state-off { border-color: #21262d; }
.state-on { border-color: #ff9800; box-shadow: inset 0 0 0 1px rgba(255, 152, 0, 0.15); }
.state-thin { border-color: #5b6cff; box-shadow: inset 0 0 0 1px rgba(91, 108, 255, 0.15); }
.state-block { border-color: #f44336; box-shadow: inset 0 0 0 1px rgba(244, 67, 54, 0.20); }

.card-row { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }

.status-block { min-width: 110px; }
.status-label { font-size: 9px; color: #888; text-transform: uppercase; letter-spacing: 0.6px; }
.status-badge {
  display: inline-block; margin-top: 2px; padding: 2px 8px; border-radius: 3px;
  font-size: 11px; font-weight: 700;
  background: #21262d; color: #8a9bff;
}
.state-off .status-badge { background: #21262d; color: #6e7681; }
.state-on .status-badge { background: #ff9800; color: #000; }
.state-thin .status-badge { background: #5b6cff; color: #000; }
.state-block .status-badge { background: #f44336; color: #000; }

.reading-block { min-width: 88px; }
.sent-value { font-size: 16px; font-weight: 700; }
.sent-value.pos { color: #4caf50; }
.sent-value.neg { color: #ef5350; }
.sent-label { color: #777; font-size: 10px; }

.gauge-block { flex: 1; min-width: 160px; }
.gauge-track {
  position: relative; height: 8px; background: #161b22;
  border-radius: 4px; overflow: visible;
}
.gauge-zero { position: absolute; top: 0; left: 50%; width: 1px; height: 100%; background: #30363d; }
.gauge-thr {
  position: absolute; top: -2px; width: 2px; height: 12px; background: #ff9800;
  transform: translateX(-50%);
}
.gauge-now {
  position: absolute; top: -3px; width: 10px; height: 14px; border-radius: 3px;
  background: #3da9fc; transform: translateX(-50%);
}
.gauge-labels {
  display: flex; justify-content: space-between;
  font-size: 9px; color: #666; margin-top: 3px;
}
.thr-label { color: #ff9800; }

.cov-block { min-width: 110px; }
.cov-label { font-size: 9px; color: #888; text-transform: uppercase; letter-spacing: 0.6px; }
.cov-bar { position: relative; height: 8px; background: #161b22; border-radius: 4px; margin-top: 3px; }
.cov-fill { height: 100%; background: #4caf50; border-radius: 4px; transition: width 0.3s; }
.state-thin .cov-fill { background: #5b6cff; }
.cov-floor {
  position: absolute; top: -2px; width: 2px; height: 12px; background: #ff9800;
  transform: translateX(-50%);
}
.cov-value { font-size: 10px; color: #aaa; margin-top: 2px; }

.spark-block { min-width: 150px; flex: 1; }
.spark-label { font-size: 9px; color: #888; text-transform: uppercase; letter-spacing: 0.6px; }
.spark { display: block; width: 100%; height: 32px; }

.footer-row {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 6px; padding-top: 6px; border-top: 1px solid #161b22;
  font-size: 10px;
}
.hint { color: #888; }
.hint .pass { color: #4caf50; font-weight: 600; }
.hint .fail { color: #f44336; font-weight: 600; }

/* (i) icon next to "News sentiment" header */
.info-icon {
  display: inline-block; cursor: pointer; margin-left: 4px;
  width: 13px; height: 13px; line-height: 13px; text-align: center;
  border-radius: 50%; background: #21262d; color: #8a9bff;
  font-size: 9px; font-style: italic; font-weight: 600;
  user-select: none;
}
.info-icon:hover { background: #30363d; color: #c5d3ff; }

/* Info panel — comprehensive description, filter status, headlines */
.info-panel {
  margin-top: 8px; padding: 10px 12px;
  background: #11161e; border: 1px solid #21262d; border-radius: 4px;
  color: #ccc; font-size: 11px; line-height: 1.5;
  /* Restore default cursor inside the panel — the card itself is a link
     but the panel content shouldn't act like one. */
  cursor: default;
}
.info-panel p { margin: 0 0 6px 0; }
.info-panel code {
  background: #0d1117; padding: 1px 4px; border-radius: 3px;
  font-family: 'JetBrains Mono', monospace; font-size: 10px;
  color: #ccc;
}
.info-section-title {
  font-size: 10px; color: #888; text-transform: uppercase;
  letter-spacing: 0.05em; margin-top: 8px !important;
  margin-bottom: 4px !important;
}
.info-bullets { list-style: none; margin: 0; padding: 0; }
.info-bullets li { padding: 2px 0; line-height: 1.5; }
.info-bullets strong { color: #aaa; font-weight: 600; }
.info-panel .pos { color: #4caf50; font-weight: 600; }
.info-panel .neg { color: #ef5350; font-weight: 600; }
.info-panel .fail { color: #f44336; font-weight: 600; }
.info-panel .dim { color: #777; }

.headlines { list-style: none; margin: 0; padding: 0; max-height: 280px; overflow-y: auto; }
.headline-row {
  display: grid;
  grid-template-columns: 50px 50px 60px 1fr;
  gap: 6px; align-items: baseline;
  padding: 3px 0;
  border-bottom: 1px dotted #1c2128;
}
.headline-row:last-child { border-bottom: none; }
.hl-ticker { font-weight: 700; color: #c5d3ff; font-family: 'JetBrains Mono', monospace; font-size: 11px; }
.hl-sent { font-weight: 600; text-align: right; font-family: 'JetBrains Mono', monospace; font-size: 10px; }
.hl-rel { font-size: 10px; font-family: 'JetBrains Mono', monospace; }
.hl-title { color: #ccc; text-decoration: none; line-height: 1.4; }
.hl-title:hover { color: #3da9fc; text-decoration: underline; }
.info-foot { margin-top: 6px !important; font-size: 10px; }

/* Mobile-first symmetry: the card-row already wraps via flex-wrap above.
   Below 640px, stack each block to its own row so the gauge / coverage
   bar / spark each get full width and remain readable. */
@media (max-width: 640px) {
  .card-row { gap: 10px; }
  .status-block, .reading-block, .gauge-block, .cov-block, .spark-block {
    flex: 1 1 100%; min-width: 0;
  }
  .gauge-block, .cov-block, .spark-block { max-width: 100%; }
  .spark { width: 100%; height: 36px; }
  .info-panel { font-size: 12px; }
  .headline-row { grid-template-columns: 44px 50px 56px 1fr; gap: 4px; }
  .hl-title { font-size: 11px; }
}
</style>
